import { useMemo } from "react"

import { Button } from "@coval/ui"

import type { TestCaseOption } from "@/types_api/backend/transport/list_test_cases_transport_response"

import { useListTestCasesTransport } from "../transport"
import {
  filterTestCaseOptions,
  groupTestCaseSelection,
} from "../utils/testCaseSelection"
import { testCaseLibraryColumns } from "./TestCaseLibraryColumns"

export function useTestCaseLibraryPickerProps({
  selectedIds,
  onSelectionChange,
  singleSelect = false,
  enabled,
}: {
  selectedIds: string[]
  onSelectionChange: (ids: string[]) => void
  singleSelect?: boolean
  enabled: boolean
}) {
  const catalog = useListTestCasesTransport({ enabled })
  const records = catalog.data
  const byId = useMemo(
    () => new Map(records?.map((record) => [record.id, record]) ?? []),
    [records],
  )
  const selection = useMemo(
    () => groupTestCaseSelection(selectedIds, records ?? []),
    [selectedIds, records],
  )
  const ready = records !== undefined && !catalog.error

  return {
    title: "Select Test Cases",
    items: records ?? [],
    columns: testCaseLibraryColumns,
    getItemId: (record: TestCaseOption) => record.id,
    getItemLabel: (record: TestCaseOption) =>
      `${record.description || "No description"} (${record.test_set_name})`,
    matchesSearch: (record: TestCaseOption, query: string) =>
      filterTestCaseOptions([record], query).length > 0,
    selectedIds,
    onSelectionChange,
    isLoading: !catalog.error && catalog.isLoading,
    applyDisabled:
      !ready ||
      selection.missingIds.length > 0 ||
      selectedIds.length > 100 ||
      (singleSelect && selection.testSetIds.length > 1),
    isSelectionDisabled: (record: TestCaseOption) =>
      !ready ||
      selectedIds.length >= 100 ||
      !record.test_set_id ||
      (singleSelect &&
        selection.testSetIds.length > 0 &&
        !selection.testSetIds.includes(record.test_set_id)),
    reconcileSelection: (ids: string[]) => {
      const retained = selectedIds.filter((id) => ids.includes(id))
      const next = [...retained]
      let parent = groupTestCaseSelection(retained, records ?? []).testSetIds[0]
      for (const id of ids) {
        if (selectedIds.includes(id) || !ready || next.length >= 100) {
          continue
        }
        const record = byId.get(id)
        if (
          !record?.test_set_id ||
          (singleSelect && parent && parent !== record.test_set_id)
        ) {
          continue
        }
        parent ??= record.test_set_id
        next.push(id)
      }
      return next
    },
    rowAttributes: (record: TestCaseOption) => ({
      "data-testid": `test-case-option-${record.id}`,
    }),
    searchPlaceholder: "Search test cases",
    banner:
      catalog.error || selection.missingIds.length > 0 ? (
        <>
          {catalog.error && (
            <div role="alert" className="text-sm text-coval-danger-text">
              Could not load test cases.{" "}
              <Button
                variant="secondary"
                onClick={() => void catalog.refetch()}
              >
                Retry
              </Button>
            </div>
          )}
          {selection.missingIds.length > 0 && (
            <div role="alert" className="text-sm text-coval-danger-text">
              Unavailable selected cases: {selection.missingIds.join(", ")}.
              Remove them from the selection to continue.
            </div>
          )}
        </>
      ) : undefined,
    createAction: (
      <span className="text-sm text-coval-text-muted">
        {selectedIds.length} / 100 selected
      </span>
    ),
    emptyState: (
      <p className="text-sm text-coval-text-muted">
        No test cases available in this workspace.
      </p>
    ),
    searchEmptyState: (
      <p className="text-sm text-coval-text-muted">
        No test cases match your search.
      </p>
    ),
    "data-testid": "test-case-library-picker-dialog",
  }
}
